import { useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import io from "socket.io-client";
import { useSelector, useDispatch } from "react-redux";
import {
  MdNotifications,
  MdAccountCircle,
  MdDarkMode,
  MdLightMode,
  MdLogout,
  MdKeyboardArrowDown,
  MdMenu,
} from "react-icons/md";
import { IoMdSettings } from "react-icons/io";

import { toggleTheme } from "../store/slices/themeSlice";
import { logoutUser } from "../redux/actions/authActions";
import { toggleSidebar } from "../store/slices/uiSlice";
import { FaRegUser } from "react-icons/fa";
import LogoutModal from "./modal/LogoutModal";
import { toast } from "react-toastify";

const Header = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useSelector((state) => state.theme);
  const { user } = useSelector((state) => state.auth);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] =
    useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const API_URL = process.env.REACT_APP_SOCKET_URL;
  const [socket, setSocket] = useState(null);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/":
        return "Dashboard";
      case "/profile":
        return "Profile";
      case "/oem-partners":
        return "OEM Partners";
      case "/settings":
        return "Settings";
      case "/scan":
        return "Scan";
        case "/oem-user-details":
        return "OEM > User";
      default:
        return "Dashboard";
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setDropdownOpen(false);
  };

  const handleLogoutConfirm = () => {
    // Cleanup socket connection before logout
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }

    // Clear notifications
    setNotifications([]);
    setNotificationCount(0);

    dispatch(logoutUser());
    setShowLogoutModal(false);
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleProfilePage = () => {
    navigate("/profile");
    setDropdownOpen(false);
  };
  const handleSettingPage = () => {
    navigate("/settings");
    setDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
    setDropdownOpen(false);
  };

  // Handle notification icon click
  const handleNotificationClick = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
  };

  // Handle individual notification click
  const handleNotificationItemClick = (notification, index) => {
    navigate(notification.route);

    // Remove the clicked notification
    setNotifications((prev) => prev.filter((_, i) => i !== index));
    setNotificationCount((prev) => (prev > 0 ? prev - 1 : 0));
    setNotificationDropdownOpen(false);
  };

  const getNotificationMessage = (notification) => {
    switch (notification.type) {
      case "userVerified":
        return `A new user - ${notification.data.firstName} ${notification.data.lastName} has been verified`;
      case "scanStarted":
        return `A new scan - Scan for ${notification.data.domain}`;
      default:
        return "New notification";
    }
  };

  useEffect(() => {
    const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
    const itsalepartnerId = userData.itSalesPartner?.id || user.itSalesPartner?.id;

    if (itsalepartnerId && user?.status === 200) {
      const socketConnection = io(API_URL);
      setSocket(socketConnection);

      socketConnection.on("connect", () => {
        socketConnection.emit("joinItSalesRoom", itsalepartnerId);
      });

      socketConnection.on("userVerified", (data) => {
        setNotificationCount((prev) => prev + 1);
        setNotifications((prev) => [
          { type: "userVerified", data, route: "/scan" },
          ...prev,
        ]);
        toast.success(`${data.firstName} ${data.lastName} has been verified`);
      });

      socketConnection.on("scanStarted", (data) => {
        setNotificationCount((prev) => prev + 1);
        setNotifications((prev) => [
          { type: "scanStarted", data, route: "/single-scan" },
          ...prev,
        ]);
        toast.info(`Scan started for ${data.domain}`);
      });

      return () => {
        socketConnection.disconnect();
      };
    }
  }, [API_URL, user?.status, user.itSalesPartner?.id]);

  // Cleanup socket on component unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  function capitalizeFirst(str = "") {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <header className="header">
      <div className="header-left">
        <div
          className="header-toggle-btn"
          onClick={() => dispatch(toggleSidebar())}
        >
          <MdMenu />
        </div>
        <h1 className="page-title">{getPageTitle()}</h1>
      </div>
      <div className="header-right">
        <div className="header-actions">
          <div className="notification-dropdown" ref={notificationRef}>
            <div className="notification-btn" onClick={handleNotificationClick}>
              <MdNotifications />
              {notificationCount > 0 && (
                <span className="notification-badge">{notificationCount}</span>
              )}
            </div>
            {notificationDropdownOpen && (
              <div
                className="notification-dropdown-menu"
                style={{
                  width: "300px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                <div>
                  <h6
                    className="notification-header text-center text-white p-2 mb-0"
                    style={{
                      borderBottom: "2px solid #ddd",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  >
                    Notifications
                  </h6>
                </div>
                {notifications.length === 0 ? (
                  <div className="notification-item no-notifications">
                    No notifications
                  </div>
                ) : (
                  <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {notifications.map((notification, index) => (
                      <div
                        key={index}
                        className="notification-item"
                        onClick={() =>
                          handleNotificationItemClick(notification, index)
                        }
                      >
                        <span className="notification_active"></span>{" "}
                        {getNotificationMessage(notification)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="profile-dropdown" ref={dropdownRef}>
            <div
              className="profile-btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {/* <MdAccountCircle /> */}
              <span className="user-name">
                {capitalizeFirst(
                  user?.itSalesPartner?.companyName ||
                    user?.companyName ||
                    user?.name ||
                    "It Sales Partner"
                )}
              </span>
              <MdKeyboardArrowDown
                className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
              />
            </div>
            {dropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={handleProfilePage}>
                  <FaRegUser />
                  Profile
                </div>
                <div className="dropdown-item" onClick={handleSettingPage}>
                  <IoMdSettings />
                  Setting
                </div>
                <button
                  className="dropdown-item logout"
                  onClick={handleLogoutClick}
                >
                  <MdLogout />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
      />
    </header>
  );
};

export default Header;
